import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Modal, Descriptions, Input, Skeleton, Space, Typography, notification } from 'antd';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';

const { Text } = Typography;
const { TextArea } = Input;

const CODES = lib.ENROLLEE_ARCHIVE_CODES;

/** Schedule/evidence dates arrive as YYYY-MM-DD; render them human, never raw. */
const fmtDate = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format('DD MMM YYYY') : null;
};

/**
 * Contract C1: `consumed_in_cycle` is true iff at least one evidence entry has
 * found=true, so this should never come back empty when the seat is retained.
 * The empty case is kept as a defensive net only — see the fallback copy below.
 */
const foundEvidence = (evidence) => Object.entries(evidence || {})
  .filter(([, e]) => e && e.found)
  .map(([kind, e]) => ({ kind, ...e }));

const EVIDENCE_LABEL = {
  checkin: 'Check-in',
  appointment: 'Completed appointment',
};

/**
 * The consequence sentence. This is the whole point of the feature: the
 * operator has to know BEFORE committing whether the seat comes back, because
 * "why can't I add a replacement?" is otherwise an unanswerable support ticket.
 */
const ConsequenceBody = ({ preview }) => {
  const retained = preview?.slot_effect === 'retained';
  const policy = preview?.policy_no || 'this policy';
  const name = preview?.full_name || 'This enrollee';

  if (!retained) {
    return (
      <Alert
        type="success"
        showIcon
        message="This frees 1 enrollment slot"
        description={(
          <>
            Archiving <Text strong>{name}</Text> returns their slot to policy{' '}
            <Text strong>{policy}</Text>. You can add a replacement immediately.
          </>
        )}
      />
    );
  }

  const evidence = foundEvidence(preview?.evidence);
  const returnsOn = fmtDate(preview?.slot_returns_on);

  return (
    <Alert
      type="warning"
      showIcon
      message="This does NOT free an enrollment slot"
      description={(
        <Space direction="vertical" size={6} style={{ display: 'flex' }}>
          <span>
            <Text strong>{name}</Text> used care in the current cycle, so their slot on policy{' '}
            <Text strong>{policy}</Text> stays held. <Text strong>No replacement can be added
            in their place</Text> until it returns.
          </span>

          {evidence.length > 0 ? (
            <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
              {evidence.map((e) => (
                <li key={e.kind}>
                  {EVIDENCE_LABEL[e.kind] || e.kind}
                  {fmtDate(e.first_date) ? <> on <Text strong>{fmtDate(e.first_date)}</Text></> : null}
                  {e.reference ? <> (<Text code>{e.reference}</Text>)</> : null}
                  {Number(e.count) > 1 ? <Text type="secondary"> · {e.count} in total</Text> : null}
                </li>
              ))}
            </ul>
          ) : null}

          <span>
            {returnsOn
              ? <>The slot returns at renewal on <Text strong>{returnsOn}</Text>.</>
              : <>The slot returns at renewal.</>}
          </span>
        </Space>
      )}
    />
  );
};

const SlotSummary = ({ preview }) => {
  const before = preview?.slots_before || {};
  const after = preview?.slots_after || {};
  return (
    <Descriptions size="small" column={2} bordered style={{ marginTop: 12 }}>
      <Descriptions.Item label="Enrollee">{preview?.full_name || '—'}</Descriptions.Item>
      <Descriptions.Item label="Insurance no.">{preview?.insurance_no || '—'}</Descriptions.Item>
      <Descriptions.Item label="Policy">{preview?.policy_no || '—'}</Descriptions.Item>
      <Descriptions.Item label="Role">{preview?.role || '—'}</Descriptions.Item>
      <Descriptions.Item label="Slots now">
        {Number(before?.total_remaining ?? 0)} remaining
      </Descriptions.Item>
      <Descriptions.Item label="Slots after archiving">
        <Text strong>{Number(after?.total_remaining ?? 0)} remaining</Text>
      </Descriptions.Item>
    </Descriptions>
  );
};

/**
 * Archive confirm dialog. Calls the non-mutating preview first and states the
 * real consequence; never a generic "are you sure".
 */
export const EnrolleeArchiveModal = ({
  open,
  insuranceNo,
  onClose,
  onArchived,
  modalZIndex = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [reason, setReason] = useState('');

  const zIndexProps = modalZIndex != null ? { zIndex: modalZIndex + 5 } : {};

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const res = await lib.archiveEnrolleePreview(insuranceNo);
    if (res?.ok) {
      setPreview(res.data || null);
    } else {
      setLoadError(res?.message || 'Could not load the archive preview.');
      setPreview(null);
    }
    setLoading(false);
  }, [insuranceNo]);

  useEffect(() => {
    if (open && insuranceNo) {
      setReason('');
      loadPreview();
    }
  }, [open, insuranceNo, loadPreview]);

  /** Surface a failed archive/unarchive by its contract code, not by HTTP alone. */
  const reportFailure = (res) => {
    const code = res?.code || null;
    if (code === CODES.UNAVAILABLE) {
      // Deploy window: code is live but the migration has not run. The operator
      // did nothing wrong and a retry later succeeds — not a validation error.
      notification.warning({
        message: 'Archiving is temporarily unavailable',
        description: 'The archive feature is still being rolled out. Nothing was changed — please try again shortly.',
        placement: 'topLeft',
      });
      return;
    }
    if (code === CODES.ALREADY_ARCHIVED) {
      notification.info({
        message: 'This enrollee is already archived.',
        placement: 'topLeft',
      });
      onArchived?.();
      onClose?.();
      return;
    }
    notification.error({
      message: res?.message || 'Failed to archive enrollee',
      placement: 'topLeft',
    });
  };

  const submitArchive = async (force = false) => {
    setSubmitting(true);
    const res = await lib.archiveEnrollee(insuranceNo, { reason, force });
    setSubmitting(false);

    if (res?.ok) {
      const retained = res?.data?.slot_effect === 'retained';
      notification.success({
        message: res?.message || 'Enrollee archived',
        description: retained
          ? 'The enrollment slot stays held until renewal.'
          : 'The enrollment slot has been returned to the policy.',
        placement: 'topLeft',
      });
      onArchived?.();
      onClose?.();
      return;
    }

    // C3: a principal with dependents must NOT be cascaded automatically. Ask
    // again, in different words, naming the count — then re-fire with force.
    if (res?.code === CODES.BLOCKED) {
      const count = Number(res?.data?.blocking?.dependent_count || 0);
      Modal.confirm({
        title: 'Also archive their dependents?',
        centered: true,
        okText: count > 0 ? `Archive all ${count + 1} people` : 'Archive all',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        content: (
          <Space direction="vertical" size={8} style={{ display: 'flex' }}>
            <span>
              {preview?.full_name || 'This enrollee'} is a principal with{' '}
              <Text strong>{count} dependent{count === 1 ? '' : 's'}</Text> still active.
            </span>
            <span>
              Continuing archives the principal <Text strong>and every dependent</Text>. Each one is
              assessed separately, so some slots may return while others stay held until renewal.
            </span>
          </Space>
        ),
        onOk: () => submitArchive(true),
        ...zIndexProps,
      });
      return;
    }

    reportFailure(res);
  };

  const retained = preview?.slot_effect === 'retained';

  return (
    <Modal
      open={open}
      centered
      destroyOnClose
      title={`Archive enrollee ${insuranceNo || ''}`}
      okText={retained ? 'Archive and hold the slot' : 'Archive enrollee'}
      okButtonProps={{ danger: retained, disabled: loading || Boolean(loadError) }}
      cancelText="Cancel"
      confirmLoading={submitting}
      onOk={() => submitArchive(false)}
      onCancel={onClose}
      width={620}
      {...zIndexProps}
    >
      {loading ? <Skeleton active paragraph={{ rows: 4 }} /> : null}

      {!loading && loadError ? (
        <Alert type="error" showIcon message="Could not load the archive preview" description={loadError} />
      ) : null}

      {!loading && !loadError && preview ? (
        <Space direction="vertical" size={12} style={{ display: 'flex' }}>
          <ConsequenceBody preview={preview} />

          {preview?.blocking ? (
            <Alert
              type="info"
              showIcon
              message={`Principal with ${Number(preview.blocking.dependent_count || 0)} active dependent(s)`}
              description="You will be asked separately whether to archive their dependents too."
            />
          ) : null}

          <SlotSummary preview={preview} />

          <div>
            <Text type="secondary">Reason (optional)</Text>
            <TextArea
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Recorded on the archive for audit."
            />
          </div>
        </Space>
      ) : null}
    </Modal>
  );
};

export default EnrolleeArchiveModal;
